import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { ToastTemplateBackendFeedback } from '@/components/molecules/ToastTemplate';
import { useAuth } from '@/contexts/auth';
import { useFeedbackController_ImageFeedback } from '@/services/hooks';
import { getLabelByValue } from '@/utils/falseDetectionOptions';

import type { ImageFeedback } from './types';

export interface UseCommitImageFeedbackArgs {
    /** Where the report belongs. Exactly one of these is set, depending on the page. */
    cageId?: number;
    bioscopeId?: number;
    /**
     * Distinguishes this page's toast from another's, so two pages cannot replace each
     * other's message.
     */
    toastId?: string;
}

/**
 * Reports a wrong detection on an image.
 *
 * The same twenty-five lines were copy-pasted into seven route containers: attach the
 * reporting user, attach the cage (or, in one case, the bioscope), fire the generated
 * mutation, then raise a thank-you toast naming the reported tag. Six of the seven built
 * that toast from a hardcoded English string; only HealthWoundsBodyInsightContainer used
 * the translated key that exists in all three locales, and only it handled a failed
 * request. This is that version, generalised.
 */
export function useCommitImageFeedback({
    cageId,
    bioscopeId,
    toastId = 'detection-feedback',
}: UseCommitImageFeedbackArgs) {
    const { t } = useTranslation('healthModule');
    const { t: tGlobal } = useTranslation();
    const { user } = useAuth();
    const { mutateAsync: reportFeedbackAsync } = useFeedbackController_ImageFeedback();

    return useCallback(
        async (feedback: ImageFeedback): Promise<void> => {
            const requestBody = {
                ...feedback,
                reportingUserId: user?.id ?? feedback.reportingUserId,
                cageId: cageId ?? feedback.cageId,
                bioscopeId: bioscopeId ?? feedback.bioscopeId,
            };

            try {
                const { status, data } = await reportFeedbackAsync({ requestBody });

                if (status) {
                    ToastTemplateBackendFeedback({
                        content: t('wounds.detectionFeedbackThanks', {
                            firstName: user?.firstName ?? '',
                            tag: tGlobal(getLabelByValue(data.tag) || ''),
                        }),
                        toastId,
                        httpStatusCode: status,
                    });
                }
            } catch {
                // Six of the seven copies had no catch at all, so a failed report was
                // silent and the user had no way to know it had not been sent.
                ToastTemplateBackendFeedback({ toastId, httpStatusCode: 500, content: '' });
            }
        },
        [bioscopeId, cageId, reportFeedbackAsync, t, tGlobal, toastId, user]
    );
}
